import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { getAttendanceSummary } from "../../../../services/StudentPortal/StudentAttendanceService";
import { getEvents } from "../../../../services/StudentPortal/StudentEventService";
import { getWordOfTheDay } from "../../../../services/StudentPortal/StudentWordOfTheDayService";
import { getHomework } from "../../../../services/StudentPortal/StudentHomeworkService";
import { getCirculars } from "../../../../services/StudentPortal/StudentCircularService";
import { handleApiError } from "../../../../utils/errorHandler";
import { unwrap, unwrapList } from "../../utils/studentPortalUtils";
import { selectUpcomingEvents } from "../../utils/studentEventUtils";

const EMPTY_DATA = {
  attendancePercent: null,
  homeworkCount: 0,
  circularCount: 0,
  upcomingEvents: [],
  wordOfTheDay: null,
};

// The attendance summary endpoint's exact field name isn't confirmed against
// the real backend yet, so this reads every reasonable candidate instead of
// assuming one.
// Confirmed against the real backend (2026-10-22): attendance_percentage.
const extractAttendancePercent = (response) => {
  const summary = unwrap(response, null);
  if (!summary) return null;
  const value = summary.attendance_percentage ?? summary.percentage ?? summary.present_percentage ?? null;
  return value === null || value === undefined ? null : Number(value);
};

// A "word of the day" record can come back as a single object or as the
// first item of a list depending on the backend's filters — same
// normalization as useStudentWordOfTheDay.js's standalone page hook. Without
// this, an array-wrapped response left `wordOfTheDay` as the raw array here
// (unlike the standalone page), so every field read came back empty and the
// Dashboard widget looked blank even though the dedicated page worked.
const normaliseWord = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (Array.isArray(data?.data)) return data.data[0] || null;
  return data;
};

// Homework / circular endpoints may return a plain array, a paginated
// { data: [...] } shape, or a { total } / { count } summary — count
// defensively instead of assuming one shape.
const countOf = (response) => {
  const data = unwrap(response, null);
  if (Array.isArray(data)) return data.length;
  if (Array.isArray(data?.data)) return data.data.length;
  if (typeof data?.total === "number") return data.total;
  if (typeof data?.count === "number") return data.count;
  return unwrapList(response).length;
};

/**
 * Loads everything the Student Dashboard tiles / events list / word-of-the-day
 * panel need, in parallel. Each call is wrapped so one endpoint failing
 * (e.g. fees has no backend yet, word-of-the-day might 404 some days) never
 * blocks the others from rendering.
 *
 * Confirmed against the real backend (2026-10-22): attendance-summary and
 * circulars need student_id + program_id + session_id; word-of-the-day needs
 * student_id + current_date; homework needs program_id + homework_date
 * (today's date here, since the dashboard tile is just a count). events'
 * exact requirements are unconfirmed — program_id/session_id are sent by
 * analogy with the other list endpoints, and the call is non-fatal either way.
 */
export const useStudentDashboard = (studentId, programId, sessionId) => {
  const [data, setData] = useState(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    if (!studentId) return;
    setIsLoading(true);
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const listParams = { student_id: studentId, program_id: programId, session_id: sessionId };
      const [attendanceRes, eventsRes, wordRes, homeworkRes, circularRes] = await Promise.all([
        programId && sessionId ? getAttendanceSummary(listParams).catch(() => null) : null,
        getEvents(listParams).catch(() => null),
        getWordOfTheDay({ student_id: studentId, current_date: today }).catch(() => null),
        programId ? getHomework({ program_id: programId, homework_date: today }).catch(() => null) : null,
        programId && sessionId ? getCirculars(listParams).catch(() => null) : null,
      ]);

      setData({
        attendancePercent: attendanceRes ? extractAttendancePercent(attendanceRes) : null,
        homeworkCount: homeworkRes ? countOf(homeworkRes) : 0,
        circularCount: circularRes ? countOf(circularRes) : 0,
        upcomingEvents: eventsRes ? selectUpcomingEvents(unwrapList(eventsRes)) : [],
        wordOfTheDay: wordRes ? normaliseWord(unwrap(wordRes, null)) : null,
      });
    } catch (error) {
      // Not expected (the per-call .catch above already absorbs individual
      // failures) but kept as a safety net around the state update itself.
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [studentId, programId, sessionId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, isLoading };
};
